import { Key } from "@solid-primitives/keyed"
import { useWindowSize } from "@solid-primitives/resize-observer"
import { ReactiveSet } from "@solid-primitives/set"
import { FaSolidSquare, FaSolidSquareCheck, FaSolidX } from "solid-icons/fa"
import { Accessor, batch, Component, createEffect, createMemo, createSignal, Match, Switch, type JSX, type JSXElement } from "solid-js"
import { createMutable } from "solid-js/store"
import createEngineForUI from "../lib/core/engine/createEngineForUI"
import type { Engine } from "../lib/core/engine/Engine.type"
import createSampleEntity from "../lib/core/entity/createSampleEntity"
import type { EntityTypeMeta } from "../lib/core/EntityTypeMeta"
import type { PlayerId } from "../lib/core/player/PlayerId"
import closeDropDownWhenOtherDropDownOpens from "../lib/event/closeDropDownWhenOtherDropDownOpens"
import { EventResult } from "../lib/event/event"
import { InputActionType } from "../lib/event/InputRouter/InputActionType"
import KeyboardShortcut from "../lib/event/InputRouter/KeyboardShortcut.component"
import { mountInputActionListenerWhen } from "../lib/event/InputRouter/mountInputActionListener"
import Button from "../lib/jsx/Button"
import firstIfOnlyOne from "../lib/ldsh/firstIfOnlyOne"
import isIdListSame from "../rx/signal/equals/isIdListSame"
import EngineContext from "./EngineContext"
import SingleEntityIcon3D from "./MapEditor/SingleEntityIcon3D"

type EntityTypeMetaList = Array<EntityTypeMeta>

interface Props {
  playerId: PlayerId | null,
  value: EntityTypeMetaList
  onChange: (newList: EntityTypeMetaList) => void
  list: EntityTypeMetaList,
  placeholder: JSXElement,
  hr?: boolean,
}

// Multi-select dropdown component - does not auto-clone on selection
const SelectEntityTypeMetaListDropDownButton: Component<Props> = (props) => {
  const [isDropDownOpen, setIsDropDownOpen] = createSignal<boolean>(false)
  // onMount(() => {
  //   setIsDropDownOpen(true)
  // })

  const engine: Engine = createMutable(createEngineForUI())

  function toIcon(etm: EntityTypeMeta, style: JSX.CSSProperties | undefined) {
    const sampleUnit = createSampleEntity(etm.id, props.playerId)
    return <SingleEntityIcon3D entity={sampleUnit} size={40} style={style} />
  }

  const windowSize = useWindowSize()

  // createEffect(() => {
  //   console.log('effect.props.value', deepClone(unwrap(props.value)))
  // })

  const firstDisplayNameIfOnlyOne = createMemo(() => {
    return firstIfOnlyOne(props.value)?.dname
  })

  const set = new ReactiveSet<number>()
  createEffect(() => {
    // eslint-disable-next-line solid/reactivity
    const { value } = props
    batch(() => {
      set.clear()
      value.forEach(etm => {
        set.add(etm.id)
      })
    })
  })

  const [selectedETMList, setSelectedETMList] = createSignal<EntityTypeMetaList>([], { equals: isIdListSame })
  createEffect(() => {
    setSelectedETMList(selectedList())
  })

  createEffect(() => {
    setSelectedETMList(props.list.filter(etm => set.has(etm.id)))
  })

  createEffect(() => {
    props.onChange(selectedETMList())
  })

  // createEffect(() => {
  //   console.log('selectedETMList', selectedETMList().map(e => e.dname))
  // })

  function toggle(etm: EntityTypeMeta): void {
    if (set.has(etm.id)) {
      set.delete(etm.id)
    } else {
      set.add(etm.id)
    }
    props.onChange(selectedList())
  }

  function selectAll(): void {
    batch(() => {
      props.list.forEach(etm => {
        set.add(etm.id)
      })
    })
    props.onChange(selectedList())
  }

  function selectNone(): void {
    set.clear()
    props.onChange(selectedList())
  }

  const isAllSelected = createMemo(() => {
    return isIdListSame(props.list, selectedETMList())
  })

  const isNoneSelected = createMemo(() => {
    return set.size === 0
  })

  function selectedList() {
    return props.list.filter(etm => set.has(etm.id))
  }

  // eslint-disable-next-line solid/reactivity
  mountInputActionListenerWhen(InputActionType.Escape, isDropDownOpen, () => {
    setIsDropDownOpen(false)
    return EventResult.Stop
  })

  return <EngineContext.Provider value={engine}>
    <div class="dropdown">
      <button class="btn btn-secondary dropdown-toggle w-100" type="button" aria-expanded="false"
        style={{ 'min-height': '3.4rem' }}
        onClick={() => { setIsDropDownOpen(!isDropDownOpen()) }}
      >
        <Switch>
          <Match when={isNoneSelected()}>
            {props.placeholder || 'None'}
          </Match>
          <Match when={isAllSelected()}>
            All ({props.list.length})
          </Match>
          <Match when={true}>
            <Key each={props.list} by="id" children={(etm: Accessor<EntityTypeMeta>) => {
              return <>{toIcon(etm(), {
                display: set.has(etm().id) ? 'inline' : 'none',
                'margin-left': '0.5rem',
              })}</>
            }} /> {firstDisplayNameIfOnlyOne()}
          </Match>
        </Switch>
        
      </button>
      <ul class="dropdown-menu" classList={{ 'dropdown-menu': true, show: isDropDownOpen(), 'w-100': true }}
        ref={(ref) => {
          createEffect(() => {
            // read some signals for triggering
            isDropDownOpen()
            const windowHeight = windowSize.height
            if (ref) {
              const bcr = ref.getBoundingClientRect()
              const maxHeightStr = `${parseInt(((windowHeight * 0.9) - bcr.top) as unknown as string)}px`
              ref.style.setProperty('max-height', maxHeightStr)
              ref.style.setProperty('overflow-y', 'scroll')
            }
          })

          closeDropDownWhenOtherDropDownOpens(ref, isDropDownOpen, setIsDropDownOpen)
        }}>
        <li class="dropdown-item">
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <Button class="btn" disabled={isAllSelected()} onClick={selectAll}
                ariaLabel="Select All"
              >All</Button>
              <Button class="btn" disabled={isNoneSelected()} onClick={selectNone}
                ariaLabel="Select None"
              >None</Button>
            </div>
            <button 
              class="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={() => setIsDropDownOpen(false)}
              aria-label="Close dropdown"
              title="Close"
            >
              <FaSolidX />
              <KeyboardShortcut action={InputActionType.Escape} />
            </button>
          </div>
        </li>
        {props.hr && <hr />}
        <Key each={props.list} by="id">
          {(etm: Accessor<EntityTypeMeta>, index: Accessor<number>) => {
            const showHR = createMemo(() => {
              if (props.hr) {
                const prevEtm = props.list[index() - 1]
                if (prevEtm) {
                  return prevEtm.group_id !== etm().group_id
                }
              }
            })
            const isSelected = () => set.has(etm().id)
            return <>
              {showHR() && <hr />}
              <li classList={{ 'bg-secondary bg-opacity-25': isSelected() }}>
                <button class="dropdown-item" type="button"
                  onClick={() => {
                    toggle(etm())
                  }}
                >
                  {isSelected() ? <FaSolidSquareCheck /> : <FaSolidSquare />}
                  {toIcon(etm(), {
                    'margin': '0 0.25rem',
                  })}{etm().dname}
                </button>
              </li>
            </>
          }}
        </Key>
      </ul>
    </div>
  </EngineContext.Provider>
}

export default SelectEntityTypeMetaListDropDownButton

